import { StaticDatePicker } from '@mui/x-date-pickers';
import { Box, Alert, Grid } from '@mui/material';
import { DigitalClock } from '@mui/x-date-pickers/DigitalClock';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import {
	getPreferredAssessmentEarliestTime,
	getPreferredAssessmentLatestTime,
	isPreferredAssessmentDateDisabled,
	assessmentTimeConfig,
	SYDNEY_TZ,
} from '../../utils/assessmentDateTimeCalculator';

interface AssessmentDateProps {
	onChange: (date: Date) => void;
	initialDate: Date;
	reminder: string;
}

// Assessment date/times always represent Australia/Sydney local time, regardless of the
// staff member's own device timezone (e.g. an admin working from the Philippines must see
// and pick the same Sydney wall-clock time a Sydney-based admin would). `initialDate` and
// the value passed to `onChange` are real instants; everything in between operates on a
// Sydney "wall clock" pseudo-date via date-fns-tz.
export const AssessmentDatePopUp = ({ onChange, reminder, initialDate }: AssessmentDateProps) => {
	const initialDateSydney = utcToZonedTime(initialDate, SYDNEY_TZ);

	const onDateChange = (date: Date | null): void => {
		if (date && initialDateSydney) {
			const newDateSydney = new Date(
				date.getFullYear(),
				date.getMonth(),
				date.getDate(),
				initialDateSydney.getHours(),
				initialDateSydney.getMinutes(),
			);

			onChange(zonedTimeToUtc(newDateSydney, SYDNEY_TZ));
		}
	};

	const onTimeChange = (time: Date | null): void => {
		if (time && initialDateSydney) {
			const newTimeSydney = new Date(
				initialDateSydney.getFullYear(),
				initialDateSydney.getMonth(),
				initialDateSydney.getDate(),
				time.getHours(),
				time.getMinutes(),
			);

			onChange(zonedTimeToUtc(newTimeSydney, SYDNEY_TZ));
		}
	};

	const nowSydney = utcToZonedTime(new Date(), SYDNEY_TZ);
	const minTime = getPreferredAssessmentEarliestTime(initialDateSydney, nowSydney, assessmentTimeConfig);
	const maxTime = getPreferredAssessmentLatestTime(initialDateSydney, assessmentTimeConfig);

	const disableDate = (date: Date): boolean => {
		return isPreferredAssessmentDateDisabled(date, nowSydney, assessmentTimeConfig);
	};

	return (
		<Box>
			<Alert severity="info">{reminder}</Alert>
			<Grid container spacing={6} justifyContent="center">
				<Grid size={{ md: 6 }}>
					<StaticDatePicker
						value={initialDateSydney}
						onChange={onDateChange}
						shouldDisableDate={disableDate}
						slots={{ actionBar: () => null }}
					/>
				</Grid>
				<Grid size={{ md: 6 }}>
					<DigitalClock
						skipDisabled
						maxTime={maxTime}
						minTime={minTime}
						timeStep={assessmentTimeConfig.timeStepMinutes}
						sx={{ mt: { xs: 0, md: 12 } }}
						onChange={onTimeChange}
						value={initialDateSydney}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};
